import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getSalesReportaData, formatDateForApi } from '../../utilities/api';

/**
 * Sales Reporta: closed bills only, read straight from product_billdetail, pivoted by
 * final_mop with total_paid_amount as the amount. Response shape is
 * { bills: [{ invoice_date, <mode>: amount, ..., total }], summary: { <mode>: amount, ..., total } } —
 * flatter than Sales Reports (no NC/Credit/Cancelled buckets, since non-closed bills
 * are excluded server-side), so no per-row shaping is needed beyond renaming `total`.
 */
const shapeRows = (bills) =>
    (Array.isArray(bills) ? bills : []).map((row) => {
        const { total, ...rest } = row;
        return { ...rest, Total: total ?? 0 };
    });

const shapeSummary = (summary) => {
    const flat = {};
    Object.entries(summary || {}).forEach(([key, value]) => {
        const label = key === 'total' ? 'Total_Total' : `Total_${key}`;
        flat[label] = value;
    });
    return flat;
};

export const fetchSalesReportaData = createAsyncThunk(
    'salesReporta/fetchSalesReportaData',
    async ({ fromDate, toDate, branchId }, { rejectWithValue }) => {
        try {
            const resolvedBranchId = branchId || 'All';
            const clientId = localStorage.getItem('clientid') || '';
            const rolename = localStorage.getItem('role') || '';
            const payload = {
                from_date: fromDate ? formatDateForApi(fromDate) : '',
                to_date: toDate ? formatDateForApi(toDate) : '',
                branch_id: resolvedBranchId,
                rolename,
                clientname: clientId,
                client_id: clientId
            };
            const data = await getSalesReportaData(payload);
            if (data?.error) {
                return rejectWithValue(data.error);
            }
            return data;
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to fetch sales reporta data');
        }
    }
);

const initialState = {
    selectedBranch: null,
    fromDate: null,
    toDate: null,

    // Snapshot of the filters as of the last Apply click. Branch Analysis (and
    // anything else that shouldn't refetch on every date-picker keystroke) reads
    // these instead of the live filters above, which update on every onChange.
    appliedFromDate: null,
    appliedToDate: null,
    appliedBranch: null,

    salesData: [],
    summary: {},
    paymentModes: [],
    orgName: '',
    branchTitle: '',
    startingInvoiceNo: '',
    endingInvoiceNo: '',

    loading: false,
    error: null
};

const salesReportaSlice = createSlice({
    name: 'salesReporta',
    initialState,
    reducers: {
        setFromDate: (state, action) => {
            state.fromDate = action.payload;
        },
        setToDate: (state, action) => {
            state.toDate = action.payload;
        },
        setSelectedBranch: (state, action) => {
            state.selectedBranch = action.payload;
        },
        // Snapshots the current filters as "applied" — call on Apply/Search click.
        applyFilters: (state) => {
            state.appliedFromDate = state.fromDate;
            state.appliedToDate = state.toDate;
            state.appliedBranch = state.selectedBranch;
        },
        resetFilters: (state) => {
            state.fromDate = null;
            state.toDate = null;
            state.selectedBranch = null;
            state.appliedFromDate = null;
            state.appliedToDate = null;
            state.appliedBranch = null;
            state.salesData = [];
            state.summary = {};
            state.paymentModes = [];
            state.orgName = '';
            state.branchTitle = '';
            state.startingInvoiceNo = '';
            state.endingInvoiceNo = '';
            state.error = null;
        },
        clearError: (state) => {
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSalesReportaData.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.salesData = [];
            })
            .addCase(fetchSalesReportaData.fulfilled, (state, action) => {
                state.loading = false;
                const bills = action.payload?.bills || [];
                state.salesData = shapeRows(bills);
                state.summary = shapeSummary(action.payload?.summary || {});
                // discount_amount/discount_qty/nc_amount/nc_qty are fixed report fields (see
                // Content component), not payment mode names — excluded here so they don't
                // get rendered a second time as bogus "mode" columns.
                const modeSet = new Set();
                const nonModeKeys = new Set(['invoice_date', 'total', 'discount_amount', 'discount_qty', 'nc_amount', 'nc_qty']);
                bills.forEach((row) => {
                    Object.keys(row).forEach((k) => {
                        if (nonModeKeys.has(k)) return;
                        modeSet.add(k);
                    });
                });
                state.paymentModes = Array.from(modeSet);
                state.orgName = action.payload?.org_name || '';
                state.branchTitle = action.payload?.branch_title || '';
                state.startingInvoiceNo = action.payload?.starting_invoice_no || '';
                state.endingInvoiceNo = action.payload?.ending_invoice_no || '';
            })
            .addCase(fetchSalesReportaData.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                state.salesData = [];
                state.summary = {};
                state.paymentModes = [];
                state.startingInvoiceNo = '';
                state.endingInvoiceNo = '';
            });
    }
});

export const { setFromDate, setToDate, setSelectedBranch, applyFilters, resetFilters, clearError } = salesReportaSlice.actions;

export const selectSalesData = (state) => state.salesReporta.salesData;
export const selectSummary = (state) => state.salesReporta.summary;
export const selectPaymentModes = (state) => state.salesReporta.paymentModes;
export const selectLoading = (state) => state.salesReporta.loading;
export const selectSelectedBranch = (state) => state.salesReporta.selectedBranch;
export const selectFromDate = (state) => state.salesReporta.fromDate;
export const selectToDate = (state) => state.salesReporta.toDate;
export const selectAppliedFromDate = (state) => state.salesReporta.appliedFromDate;
export const selectAppliedToDate = (state) => state.salesReporta.appliedToDate;
export const selectAppliedBranch = (state) => state.salesReporta.appliedBranch;
export const selectError = (state) => state.salesReporta.error;
export const selectOrgName = (state) => state.salesReporta.orgName;
export const selectBranchTitle = (state) => state.salesReporta.branchTitle;
export const selectStartingInvoiceNo = (state) => state.salesReporta.startingInvoiceNo;
export const selectEndingInvoiceNo = (state) => state.salesReporta.endingInvoiceNo;

export default salesReportaSlice.reducer;
